import { useMemo } from 'react'

import {
    eqBoolFilter,
    eqStringFilter,
    iLikeStringFilter,
    inStringArrFilter,
} from '~utils/query-helpers'
import {
    QueryInput,
    AuthorityLevel,
    ClinicFieldsFragment,
    PracticeManagementSystem,
} from '~graphql/generated/graphql'
import { PAGE_SIZES, PageSizeType, SetStateFn } from '~config/constants'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { usePageControls } from '~utils/hooks/use-page-controls'

export function useClinicianQueryInput(
    clinic: ClinicFieldsFragment,
): ClinicianQueryInputValues & {
    queryInput: QueryInput[]
} {
    const [query, setQuery, debouncedQuery] = useStateWithDebounce<string>('')
    const [authorityLevel, setAuthorityLevel, debouncedAuthorityLevel] =
        useStateWithDebounce<AuthorityLevel[]>([])
    const [showInactive, setShowInactive, debouncedInactive] =
        useStateWithDebounce<boolean>(false)
    const [
        showContributesToFillRate,
        setShowContributesToFillRate,
        debouncedShowContributesToFillRate,
    ] = useStateWithDebounce<boolean>(false)
    const [showPmsInactive, setShowPmsInactive, debouncedShowPmsInactive] =
        useStateWithDebounce<boolean>(false)
    const [
        showOtherLocationClinicians,
        setShowOtherLocationClinicians,
        debouncedShowOtherLocationClinicians,
    ] = useStateWithDebounce<boolean>(false)
    const { page, setPage, pageSize, setPageSize } = usePageControls(
        PAGE_SIZES[1],
        JSON.stringify([
            query,
            authorityLevel,
            showInactive,
            showContributesToFillRate,
            showPmsInactive,
            showOtherLocationClinicians,
        ]),
    )

    const queryInput: QueryInput[] = useMemo(() => {
        const retInput: QueryInput[] = [eqStringFilter('clinic', clinic.id)]

        if (debouncedQuery) {
            retInput.push(iLikeStringFilter('name', debouncedQuery))
        }

        if (debouncedAuthorityLevel.length) {
            retInput.push(
                inStringArrFilter('authority_level', debouncedAuthorityLevel),
            )
        }

        if (!debouncedInactive) {
            retInput.push(eqBoolFilter('is_assignable_in_pms', true))
            retInput.push(eqBoolFilter('is_active', true))
        }

        if (debouncedShowContributesToFillRate) {
            retInput.push(eqBoolFilter('does_contribute_to_fill_rate', true))
        }

        if (!debouncedShowPmsInactive) {
            retInput.push(eqBoolFilter('pms_is_active', true))
        }

        if (
            !!clinic.pms_db &&
            !debouncedShowOtherLocationClinicians &&
            debouncedInactive &&
            allowOtherLocationFilter()
        ) {
            // Active views should include assignable clinicians from other PMS locations.
            // When inactive clinicians are included, keep the home-location filter unless explicitly disabled.
            retInput.push(eqStringFilter('pms_db', clinic.pms_db.id))
        }

        return retInput
    }, [
        clinic,
        debouncedQuery,
        debouncedAuthorityLevel,
        debouncedInactive,
        debouncedShowContributesToFillRate,
        debouncedShowPmsInactive,
        debouncedShowOtherLocationClinicians,
    ])

    function allowOtherLocationFilter() {
        return ![
            PracticeManagementSystem.Instinct,
            PracticeManagementSystem.Cornerstone,
        ].includes(clinic.pms)
    }

    return {
        queryInput,

        page,
        setPage,
        pageSize,
        setPageSize,

        query,
        setQuery,
        authorityLevel,
        setAuthorityLevel,
        showInactive,
        setShowInactive,
        showContributesToFillRate,
        setShowContributesToFillRate,
        showPmsInactive,
        setShowPmsInactive,
        showOtherLocationClinicians,
        setShowOtherLocationClinicians,
    }
}

export type ClinicianQueryInputValues = {
    page: number
    setPage: SetStateFn<number>
    pageSize: PageSizeType
    setPageSize: SetStateFn<PageSizeType> | undefined

    query: string
    setQuery: SetStateFn<string>
    authorityLevel: AuthorityLevel[]
    setAuthorityLevel: SetStateFn<AuthorityLevel[]>
    showInactive: boolean
    setShowInactive: SetStateFn<boolean>
    showContributesToFillRate: boolean
    setShowContributesToFillRate: SetStateFn<boolean>
    showPmsInactive: boolean
    setShowPmsInactive: SetStateFn<boolean>
    showOtherLocationClinicians: boolean
    setShowOtherLocationClinicians: SetStateFn<boolean>
}
